import React from 'react';
import { motion } from 'framer-motion';
import { FaFolder, FaCode, FaChartBar } from 'react-icons/fa';
import './AutomationFramework.css';

const AutomationFramework = () => {
  const frameworkFeatures = [
    {
      icon: '🏗️',
      title: 'Page Object Model',
      description: 'Maintainable and reusable page objects for better code organization'
    },
    {
      icon: '📊',
      title: 'Data-Driven Testing',
      description: 'Excel/CSV/JSON data sources for parameterized test execution'
    },
    {
      icon: '🔄',
      title: 'CI/CD Integration',
      description: 'Jenkins/GitHub Actions integration for automated test execution'
    },
    {
      icon: '📝',
      title: 'Detailed Reporting',
      description: 'Extent Reports and Allure for comprehensive test results'
    },
    {
      icon: '🎯',
      title: 'Cross-Browser Testing',
      description: 'Support for Chrome, Firefox, Edge, and Safari browsers'
    },
    {
      icon: '⚙️',
      title: 'Configuration Management',
      description: 'Environment-specific configurations and test data management'
    }
  ];

  const folderStructure = [
    { name: 'pages/', description: 'Page Object classes', icon: <FaFolder /> },
    { name: 'tests/', description: 'Test cases and test suites', icon: <FaFolder /> },
    { name: 'utils/', description: 'Helper functions and utilities', icon: <FaFolder /> },
    { name: 'data/', description: 'Test data files (CSV, JSON, Excel)', icon: <FaFolder /> },
    { name: 'reports/', description: 'Test execution reports', icon: <FaFolder /> },
    { name: 'config/', description: 'Configuration files', icon: <FaFolder /> },
    { name: 'conftest.py', description: 'PyTest fixtures and hooks', icon: <FaCode /> },
    { name: 'requirements.txt', description: 'Python dependencies', icon: <FaCode /> }
  ];

  const executionFlow = [
    { step: '1', title: 'Test Discovery', description: 'PyTest discovers all test cases' },
    { step: '2', title: 'Setup', description: 'Browser initialization and configuration' },
    { step: '3', title: 'Execution', description: 'Test cases run with page objects' },
    { step: '4', title: 'Reporting', description: 'Generate detailed test reports' },
    { step: '5', title: 'Cleanup', description: 'Browser closure and resource cleanup' }
  ];

  return (
    <section id="automation-framework" className="automation-framework">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Automation Framework</h2>
          <p className="section-subtitle">
            Robust and scalable test automation framework built with industry best practices
          </p>
        </motion.div>

        <div className="framework-content">
          <motion.div
            className="framework-overview"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>Framework Architecture</h3>
            <div className="architecture-diagram">
              <div className="arch-layer">
                <div className="arch-box">Test Cases (PyTest)</div>
              </div>
              <div className="arch-arrow">↓</div>
              <div className="arch-layer">
                <div className="arch-box">Page Objects (POM)</div>
              </div>
              <div className="arch-arrow">↓</div>
              <div className="arch-layer">
                <div className="arch-box">Selenium WebDriver</div>
              </div>
              <div className="arch-arrow">↓</div>
              <div className="arch-layer">
                <div className="arch-box">Browser (Chrome/Firefox/Edge)</div>
              </div>
            </div>

            <div className="framework-tech">
              <h4>Technologies Used</h4>
              <div className="tech-badges">
                <span className="tech-badge">Python</span>
                <span className="tech-badge">Selenium</span>
                <span className="tech-badge">PyTest</span>
                <span className="tech-badge">Page Object Model</span>
                <span className="tech-badge">Extent Reports</span>
                <span className="tech-badge">Allure</span>
                <span className="tech-badge">Jenkins</span>
                <span className="tech-badge">WebDriver</span>
                <span className="tech-badge">Git</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="framework-features"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>Key Features</h3>
            <div className="features-grid">
              {frameworkFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="folder-structure"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <h3>Folder Structure</h3>
          <div className="structure-grid">
            {folderStructure.map((item, index) => (
              <motion.div
                key={index}
                className="structure-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.05 }}
              >
                <div className="structure-icon">{item.icon}</div>
                <div className="structure-info">
                  <span className="structure-name">{item.name}</span>
                  <span className="structure-desc">{item.description}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="execution-flow"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <h3>Test Execution Flow</h3>
          <div className="flow-steps">
            {executionFlow.map((item, index) => (
              <motion.div
                key={index}
                className="flow-step"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 + index * 0.1 }}
              >
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
                {index < executionFlow.length - 1 && <div className="step-connector">→</div>}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="framework-reports"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          <h3>Test Reports Preview</h3>
          <div className="reports-grid">
            <div className="report-card">
              <FaChartBar className="report-icon" />
              <h4>Extent Reports</h4>
              <p>Detailed HTML reports with screenshots, logs, and execution timeline</p>
              <div className="report-stats">
                <span className="stat-item">✓ Pass/Fail Status</span>
                <span className="stat-item">📸 Screenshots</span>
                <span className="stat-item">⏱️ Execution Time</span>
              </div>
            </div>
            <div className="report-card">
              <FaChartBar className="report-icon" />
              <h4>Allure Reports</h4>
              <p>Interactive reports with test history, trends, and categorization</p>
              <div className="report-stats">
                <span className="stat-item">📊 Trends</span>
                <span className="stat-item">🏷️ Categories</span>
                <span className="stat-item">📈 History</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AutomationFramework;
